import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext.js';
import { Link } from 'react-router-dom';

function EditProfileForm() {
  const { token } = useAuth();
  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // --- NEW STATES FOR PHOTO UPLOAD ---
  const [isUploading, setIsUploading] = useState(false);
  const [photoMessage, setPhotoMessage] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchUserDetails = async () => {
      const API_URL = 'https://healthcard.searchisend.com/health_api/get_user_details.php';
      try {
        const response = await fetch(API_URL, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Could not fetch user details.');
        const data = await response.json();
        setFormData(data);
      } catch (error) {
        setMessage(error.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserDetails();
  }, [token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  // --- NEW HANDLER FOR PHOTO UPLOAD ---
  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setIsUploading(true);
    setPhotoMessage('Uploading photo...');

    const photoFormData = new FormData();
    photoFormData.append('profilePhoto', file);

    try {
      const response = await fetch('https://healthcard.searchisend.com/health_api/update_user_photo.php', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: photoFormData
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message);

      setPhotoMessage('Photo updated successfully!');
      // Update the profile picture in the form state
      setFormData(prev => ({ ...prev, profilePictureUrl: result.profilePictureUrl }));

      // Also update the user info in AuthContext/localStorage if possible
      // This requires a bit more logic in AuthContext, but for now just show a message.

    } catch (error) {
      setPhotoMessage(`Upload Error: ${error.message}`);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');

    const API_URL = 'https://healthcard.searchisend.com/health_api/submit_profile_update.php';
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message);

      setMessage(result.message);
      setIsSuccess(true);
    } catch (error) {
      setMessage(error.message);
      setIsSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) return <p className="text-center">Loading your profile...</p>;
  if (!formData) return <p className="text-center text-red-500">{message || 'Could not load profile data.'}</p>;

  return (
    <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-2xl border-t-4 border-brand-primary">
      <h2 className="text-3xl font-bold text-brand-primary text-center mb-6">Edit Your Profile</h2>

      {/* --- NEW PHOTO UPLOAD SECTION --- */}
      <div className="flex flex-col items-center mb-6 space-y-4">
        <img
          src={formData.profilePictureUrl ? `https://healthcard.searchisend.com${formData.profilePictureUrl}` : `https://ui-avatars.com/api/?name=${formData.firstName}+${formData.lastName}&background=8b0000&color=fff&size=128`}
          alt="Profile"
          className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
        />
        <div className="w-full max-w-sm">
          <label htmlFor="photoInput" className="block text-sm font-medium text-gray-700 mb-2">
            Update Your Profile Photo (max 2MB)
          </label>
          <input
            id="photoInput"
            type="file"
            accept="image/jpeg, image/png"
            onChange={handlePhotoUpload}
            ref={fileInputRef}
            disabled={isUploading}
            className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
          />
          {photoMessage && <p className="text-sm text-center mt-2 text-gray-600">{photoMessage}</p>}
        </div>
        <div className="border-t border-gray-200 w-full my-4"></div>
      </div>

      {isSuccess ? (
        <div className="text-center">
          <p className="text-lg text-green-700">{message}</p>
          <Link to="/my-dashboard" className="block text-center text-white bg-brand-primary hover:bg-red-900 mt-4 py-2 px-4 rounded-md">
            Back to Dashboard
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-center text-gray-500 -mt-4 mb-4">
            Update your profile information below. Changes will take effect immediately.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">First Name *</label>
              <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className="w-full p-2 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Last Name *</label>
              <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className="w-full p-2 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Email Address (Cannot be changed)</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} required readOnly className="w-full p-2 bg-gray-200 text-gray-500 rounded-md border border-gray-300 cursor-not-allowed" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Phone Number</label>
              <input type="tel" name="phoneNumber" value={formData.phoneNumber || ''} onChange={handleChange} className="w-full p-2 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">State *</label>
              <input type="text" name="state" value={formData.state} onChange={handleChange} required className="w-full p-2 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">City</label>
              <input type="text" name="city" value={formData.city || ''} onChange={handleChange} className="w-full p-2 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Profession / Job Title</label>
              <input type="text" name="profession" value={formData.profession || ''} onChange={handleChange} className="w-full p-2 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">A short bio about yourself...</label>
            <textarea name="bio" value={formData.bio || ''} onChange={handleChange} rows="3" className="w-full p-2 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary"></textarea>
          </div>

          <hr className="my-6 border-gray-200" />
          <h3 className="text-xl font-bold text-brand-primary mb-2">Health & Emergency Info</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Blood Group *</label>
              <select name="bloodGroup" value={formData.bloodGroup || ''} onChange={handleChange} required className="w-full p-2 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary">
                <option value="">Select Blood Group</option>
                <option value="A+">A+</option><option value="A-">A-</option>
                <option value="B+">B+</option><option value="B-">B-</option>
                <option value="O+">O+</option><option value="O-">O-</option>
                <option value="AB+">AB+</option><option value="AB-">AB-</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Allergies (if any)</label>
              <input type="text" name="allergies" value={formData.allergies || ''} onChange={handleChange} placeholder="e.g., Peanuts, Penicillin" className="w-full p-2 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Date of Birth *</label>
              <input type="date" name="dateOfBirth" value={formData.dateOfBirth || ''} onChange={handleChange} required className="w-full p-2 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Medical Conditions (if any)</label>
            <textarea name="medicalConditions" value={formData.medicalConditions || ''} onChange={handleChange} placeholder="e.g., Diabetes, Asthma" rows="2" className="w-full p-2 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary"></textarea>
          </div>

          <div className="bg-gray-50 p-4 rounded-md border border-gray-300 flex items-center gap-3">
            <input type="checkbox" id="isOrganDonorEdit" name="isOrganDonor" checked={formData.isOrganDonor == 1 || formData.isOrganDonor === true} onChange={handleChange} className="w-5 h-5 text-brand-primary rounded border-gray-300 focus:ring-brand-primary" />
            <label htmlFor="isOrganDonorEdit" className="text-gray-700 font-medium">I am a registered Organ Donor</label>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Current Medications & Dosages</label>
            <textarea name="currentMedications" value={formData.currentMedications || ''} onChange={handleChange} rows="2" className="w-full p-2 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary"></textarea>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Primary Care Physician Name</label>
              <input type="text" name="primaryCarePhysicianName" value={formData.primaryCarePhysicianName || ''} onChange={handleChange} className="w-full p-2 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Physician Phone Number</label>
              <input type="tel" name="primaryCarePhysicianPhone" value={formData.primaryCarePhysicianPhone || ''} onChange={handleChange} className="w-full p-2 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Major Vaccinations (e.g., COVID-19, Tetanus)</label>
            <input type="text" name="vaccinationRecords" value={formData.vaccinationRecords || ''} onChange={handleChange} className="w-full p-2 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
          </div>

          <h4 className="text-lg font-bold text-gray-700 mt-6 mb-2">Emergency Contacts</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Emergency Contact Name *</label>
              <input type="text" name="emergencyContactName" value={formData.emergencyContactName || ''} onChange={handleChange} required className="w-full p-2 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Relation (e.g., Spouse) *</label>
              <input type="text" name="emergencyContactRelation" value={formData.emergencyContactRelation || ''} onChange={handleChange} required className="w-full p-2 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Emergency Phone Number *</label>
              <input type="tel" name="emergencyContactPhone" value={formData.emergencyContactPhone || ''} onChange={handleChange} required className="w-full p-2 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
            </div>
          </div>

          <button type="submit" disabled={isSubmitting} className="w-full p-3 bg-brand-primary hover:bg-emerald-700 rounded-md font-bold text-white transition-colors disabled:bg-slate-300">
            {isSubmitting ? 'Saving Changes...' : 'Save Changes'}
          </button>
          <Link to="/my-dashboard" className="block text-center text-gray-600 hover:text-brand-primary mt-2">
            Cancel
          </Link>
          {message && !isSuccess && <p className="mt-4 text-center text-red-600">{message}</p>}
        </form>
      )}
    </div>
  );
}

export default EditProfileForm;